import {
  AGENT_IMPORT_MAX_AGENTS_PER_FILE,
  AGENT_IMPORT_MAX_DESCRIPTION_LENGTH,
  AGENT_IMPORT_MAX_FILE_BYTES,
  AGENT_IMPORT_MAX_INSTRUCTIONS_LENGTH,
  AGENT_IMPORT_MAX_NAME_LENGTH,
} from "../constants/agent-import";
import type {
  IAgentImportDraft,
  IAgentImportRowIssue,
  IParseAgentImportFileProps,
  IParsedAgentImportFile,
} from "../types/agent-import";
import {
  EAgentImportFileError,
  EAgentImportRowError,
} from "../types/agent-import";
import { parseCsv } from "./parse-csv";

type TAgentField = "name" | "description" | "instructions";

const AGENT_FIELDS: readonly TAgentField[] = [
  "name",
  "description",
  "instructions",
];

// NOTE: NSAI-6677 — exports from different platforms name the same three
// things differently. Keys are compared after lower-casing and dropping
// everything but letters and digits, so "System Prompt", "system_prompt" and
// "systemPrompt" all match "systemprompt".
const FIELD_ALIASES: Record<TAgentField, readonly string[]> = {
  name: ["name", "title", "nickname", "agentname", "assistantname", "gptname"],
  description: ["description", "desc", "summary", "about"],
  instructions: [
    "instructions",
    "systemprompt",
    "prompt",
    "system",
    "systemmessage",
    "persona",
  ],
};

// NOTE: A JSON file may wrap its list in an object; these are the wrappers
// seen in the wild (our own template uses `agents`).
const JSON_LIST_KEYS = ["agents", "assistants", "gpts", "items", "data"];

const BYTE_ORDER_MARK = String.fromCodePoint(0xfe_ff);
const CSV_EXTENSION = ".csv";
const JSON_EXTENSION = ".json";

const FIELD_LIMITS: Record<
  TAgentField,
  { max: number; reason: EAgentImportRowError }
> = {
  name: {
    max: AGENT_IMPORT_MAX_NAME_LENGTH,
    reason: EAgentImportRowError.NAME_TOO_LONG,
  },
  description: {
    max: AGENT_IMPORT_MAX_DESCRIPTION_LENGTH,
    reason: EAgentImportRowError.DESCRIPTION_TOO_LONG,
  },
  instructions: {
    max: AGENT_IMPORT_MAX_INSTRUCTIONS_LENGTH,
    reason: EAgentImportRowError.INSTRUCTIONS_TOO_LONG,
  },
};

const normalizeKey = (key: string): string =>
  key.toLowerCase().replaceAll(/[^a-z0-9]/g, "");

const resolveField = (key: string): TAgentField | null => {
  const normalized = normalizeKey(key);
  return (
    AGENT_FIELDS.find((field) => FIELD_ALIASES[field].includes(normalized)) ??
    null
  );
};

const fileErrorResult = (
  fileError: EAgentImportFileError,
): IParsedAgentImportFile => ({ agents: [], rowIssues: [], fileError });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type TAgentFields = Partial<Record<TAgentField, string>>;

// NOTE: Picks the three fields out of one entry. The first key that maps to a
// field wins, and only non-empty strings count — a number or an object in a
// "name" column is not a name.
const pickFields = (entries: [string, unknown][]): TAgentFields => {
  const fields: TAgentFields = {};
  for (const [key, value] of entries) {
    const field = resolveField(key);
    if (!field || fields[field] !== undefined || typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed) {
      fields[field] = trimmed;
    }
  }
  return fields;
};

interface IToDraftProps {
  fields: TAgentFields;
  position: number;
}

const toDraft = ({
  fields,
  position,
}: IToDraftProps): IAgentImportDraft | IAgentImportRowIssue => {
  if (!fields.name) {
    return { position, reason: EAgentImportRowError.MISSING_NAME };
  }
  for (const field of AGENT_FIELDS) {
    const limit = FIELD_LIMITS[field];
    if ((fields[field]?.length ?? 0) > limit.max) {
      return { position, reason: limit.reason };
    }
  }
  return {
    agentId: `entry-${position}`,
    name: fields.name,
    description: fields.description ?? null,
    instructions: fields.instructions ?? null,
  };
};

const isDraft = (
  value: IAgentImportDraft | IAgentImportRowIssue,
): value is IAgentImportDraft => "agentId" in value;

interface IPositionedEntry {
  fields: TAgentFields | null;
  position: number;
}

const collect = (entries: IPositionedEntry[]): IParsedAgentImportFile => {
  const agents: IAgentImportDraft[] = [];
  const rowIssues: IAgentImportRowIssue[] = [];

  entries.forEach(({ fields, position }) => {
    if (fields === null) {
      rowIssues.push({ position, reason: EAgentImportRowError.NOT_AN_OBJECT });
      return;
    }
    const draft = toDraft({ fields, position });
    if (isDraft(draft)) {
      agents.push(draft);
    } else {
      rowIssues.push(draft);
    }
  });

  if (agents.length === 0 && rowIssues.length === 0) {
    return fileErrorResult(EAgentImportFileError.NO_AGENTS);
  }
  return { agents, rowIssues, fileError: null };
};

const extractJsonList = (parsed: unknown): unknown[] | null => {
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  for (const key of JSON_LIST_KEYS) {
    const value = parsed[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  // NOTE: A single agent object on its own is a list of one.
  return Object.keys(parsed).some((key) => resolveField(key) === "name")
    ? [parsed]
    : null;
};

const parseJsonFile = (text: string): IParsedAgentImportFile => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return fileErrorResult(EAgentImportFileError.INVALID_JSON);
  }

  const list = extractJsonList(parsed);
  if (list === null) {
    return fileErrorResult(EAgentImportFileError.UNEXPECTED_JSON_SHAPE);
  }
  if (list.length > AGENT_IMPORT_MAX_AGENTS_PER_FILE) {
    return fileErrorResult(EAgentImportFileError.TOO_MANY_AGENTS);
  }

  return collect(
    list.map((entry, index) => ({
      fields: isRecord(entry) ? pickFields(Object.entries(entry)) : null,
      position: index + 1,
    })),
  );
};

// NOTE: parseCsv toggles "inside quotes" on every quote character and consumes
// an escaped `""` as a pair, so a file ends inside a quoted field exactly when
// it holds an odd number of quote characters. parseCsv itself does not report
// that: it folds every following line into the open field, which would import
// an altered prompt and silently drop the agents after it.
const hasUnclosedQuote = (text: string): boolean =>
  (text.match(/"/g)?.length ?? 0) % 2 === 1;

const isBlankRow = (row: string[]): boolean => !row.some((cell) => cell.trim());

const parseCsvFile = (text: string): IParsedAgentImportFile => {
  if (hasUnclosedQuote(text)) {
    return fileErrorResult(EAgentImportFileError.MALFORMED_CSV);
  }

  // NOTE: No row cap here — blank lines must not count against the agent limit,
  // and the file is already bounded by AGENT_IMPORT_MAX_FILE_BYTES.
  const [header = [], ...dataRows] = parseCsv({ text }).rows;

  if (!header.some((column) => resolveField(column) === "name")) {
    return fileErrorResult(EAgentImportFileError.MISSING_NAME_COLUMN);
  }

  // NOTE: Blank lines (every cell empty) are layout, not entries — but they
  // still count for an entry's position, so a reported row is the one the
  // user sees in the file.
  const entries = dataRows.flatMap((row, index) =>
    isBlankRow(row)
      ? []
      : [
          {
            fields: pickFields(
              header.map((column, columnIndex) => [
                column,
                row[columnIndex] ?? "",
              ]),
            ),
            position: index + 1,
          },
        ],
  );
  if (entries.length > AGENT_IMPORT_MAX_AGENTS_PER_FILE) {
    return fileErrorResult(EAgentImportFileError.TOO_MANY_AGENTS);
  }

  return collect(entries);
};

// NOTE: NSAI-6677 — reads an agent list exported from another platform (or
// hand-written) out of a `.json` or `.csv` file. Pure and synchronous so web
// and mobile share it; the caller reads the file into text and checks its byte
// size first (`AGENT_IMPORT_MAX_FILE_BYTES`) — `text.length` is only a second
// guard here. Invalid entries are reported per row and skipped, so one bad
// row never blocks the rest of the file.
export const parseAgentImportFile = ({
  fileName,
  text,
}: IParseAgentImportFileProps): IParsedAgentImportFile => {
  if (text.length > AGENT_IMPORT_MAX_FILE_BYTES) {
    return fileErrorResult(EAgentImportFileError.TOO_LARGE);
  }

  const content = text.startsWith(BYTE_ORDER_MARK) ? text.slice(1) : text;
  const lowerName = fileName.toLowerCase();

  if (lowerName.endsWith(JSON_EXTENSION)) {
    return parseJsonFile(content);
  }
  if (lowerName.endsWith(CSV_EXTENSION)) {
    return parseCsvFile(content);
  }
  return fileErrorResult(EAgentImportFileError.UNSUPPORTED_FORMAT);
};
